import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Message, {
  TYPE
} from '@mos-front-components/public/es/components/Message';
import Button from '@mos-cat/button';
import Spinner from '@mos-front-components/public/es/components/Spinner';

import appealExistsSelector from './appealExistsSelector';
import { findAppeal } from '../../store/appealFind/actions';
import { APPLICANT_TYPES } from '../../constants/applicant';
import {
  sendCMAAppealBusinessError,
  sendCMAAppealTechError
} from '../../helpers/CMAStats';

import './AppealExists.pcss';

const AppealExistsWarningMessage = () => (
  <Message
    type={Message.TYPE.WARNING}
    smallerText
    className="AppealExists__message"
  >
    <h5 className="AppealExists__title">Заявление является повторным</h5>
    <p>
      Ваше заявление является повторным. Заявление участника ГИА
      с&nbsp;указанным СНИЛС было подано ранее. Заявителю предоставляется
      возможность отозвать поданное заявление и&nbsp;в&nbsp;случае, если сроки
      подачи апелляции еще не&nbsp;истекли, Вы имеете право повторно подать
      заявление. Для получения разъяснений Вы можете обратиться
      в&nbsp;Региональный центр обработки информации города Москвы{' '}
      <a className="Link" rel="noopener noreferrer" href="tel:+74996539450">
        +7 499 653-94-50
      </a>
      .
    </p>
  </Message>
);

const AppealExistsErrorMessage = () => (
  <Message
    type={Message.TYPE.ERROR}
    smallerText
    className="AppealExists__message"
  >
    <h5 className="AppealExists__title">Ваше заявление является повторным</h5>
    <p>
      Заявление участника ГИА с указанным СНИЛС было подано ранее.
      В&nbsp;течение 24 часов после регистрации заявления вы можете его отозвать
      и&nbsp;в&nbsp;случае, если сроки подачи апелляции еще не&nbsp;истекли,
      имеете право повторно подать заявление. Для получения разъяснений можно
      обратиться в&nbsp;региональный центр обработки информации города Москвы
      по&nbsp;телефону:{' '}
      <a className="Link" rel="noopener noreferrer" href="tel:+74996539450">
        +7 499 653-94-50
      </a>
      .
    </p>
  </Message>
);

const StudentApplicationExistsErrorMessage = () => (
  <Message
    type={Message.TYPE.ERROR}
    smallerText
    className="AppealExists__message"
  >
    <h5 className="AppealExists__title">Заявление является повторным</h5>
    <p>
      На участника ГИА с указанным СНИЛС ранее уже была подана апелляция
      участником ГИА. Отзыв заявления доступен только участнику ГИА
      под&nbsp;своей учетной записью. Для&nbsp;получения разъяснений можно
      обратиться в&nbsp;региональный центр обработки информации города Москвы
      по&nbsp;телефону:{' '}
      <a className="Link" rel="noopener noreferrer" href="tel:+74996539450">
        +7 499 653-94-50
      </a>
      .
    </p>
  </Message>
);

const AppealExists = ({
  applicantType,
  loading,
  error,
  found,
  isAuthor,
  serviceNumber,
  canBeCanceled,
  findAppeal
}) => {
  const onRefreshClick = useCallback(() => {
    findAppeal();
  }, [findAppeal]);

  useEffect(() => {
    if (error && !loading) {
      sendCMAAppealTechError({ stage: 'step' });
    }
  }, [error, loading]);

  useEffect(() => {
    if (found && !loading) {
      sendCMAAppealBusinessError({ stage: 'step' });
    }
  }, [found, loading]);

  if (loading) {
    return (
      <div className="AppealExists__loader">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Message
        type={TYPE.ERROR}
        smallerText
        bordered
        className="AppealExists__message AppealExists__error"
      >
        <div className="AppealExists__error-title">
          По техническим причинам не удалось проверить информацию об экзамене.
        </div>
        <div className="AppealExists__error-text">
          <Button
            className="AppealExists__error-button"
            type="button"
            onClick={onRefreshClick}
          >
            Повторить
          </Button>
        </div>
      </Message>
    );
  }

  if (!found) {
    return null;
  }

  const renderMessages = () => {
    return canBeCanceled ? (
      <AppealExistsWarningMessage />
    ) : (
      <AppealExistsErrorMessage />
    );
  };

  const { parent, participant } = APPLICANT_TYPES;
  let message;

  if (applicantType === parent && !isAuthor) {
    message = <StudentApplicationExistsErrorMessage />;
  } else if (applicantType === parent && isAuthor) {
    message = renderMessages();
  } else if (applicantType === participant && !isAuthor) {
    message = (
      <Message
        type={Message.TYPE.ERROR}
        smallerText
        className="AppealExists__message"
      >
        <h5 className="AppealExists__title">Заявление является повторным</h5>
        <p>
          На участника ГИА с указанным СНИЛС ранее уже была подана апелляция
          законным представителем (родителем/ опекуном/ попечителем). Отзыв
          апелляции доступен только законному представителю под своей учетной
          записью. Для получения разъяснений можно обратиться в региональный
          центр обработки информации города Москвы по телефону:{' '}
          <a className="Link" rel="noopener noreferrer" href="tel:+74996539450">
            +7 499 653-94-50
          </a>
          .
        </p>
      </Message>
    );
  } else {
    message = renderMessages();
  }

  const showEditButton = canBeCanceled && isAuthor && serviceNumber;

  return (
    <div className="AppealExists">
      {message}

      {showEditButton && (
        <a
          href={`/services/podat-apellyatsiyu-na-rezultaty-gia/cancel/${serviceNumber}`}
          className="AppealExists__button"
        >
          <Button primary>Отозвать апелляцию</Button>
        </a>
      )}
    </div>
  );
};

AppealExists.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  found: PropTypes.bool,
  isAuthor: PropTypes.bool,
  canBeCanceled: PropTypes.bool,
  serviceNumber: PropTypes.string,
  applicantType: PropTypes.string.isRequired,
  findAppeal: PropTypes.func.isRequired
};

AppealExists.defaultProps = {
  loading: false,
  error: false,
  found: false,
  isAuthor: false,
  canBeCanceled: false,
  serviceNumber: ''
};

export default connect(appealExistsSelector, dispatch =>
  bindActionCreators(
    {
      findAppeal
    },
    dispatch
  )
)(AppealExists);
